import styled, { css, RuleSet } from 'styled-components'

import { ButtonSizeKeys, ButtonUseKeys } from './button.types'

export const Button = styled.button<{
  $use: ButtonUseKeys
  $size: ButtonSizeKeys
  $onlyBox?: boolean
  $overrideDisabled?: boolean
}>(({ theme, $use, $size, $onlyBox, $overrideDisabled }) => {
  const baseStyles = css`
    position: relative;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: fit-content;
    cursor: pointer;
    overflow: initial;
    padding: 0;
    margin: 0;
    border-radius: unset;
    user-select: none;
    border: none;
    outline: none;
    background: none;
    font-weight: 500;
    gap: ${theme.space['4']};

    &:focus-visible {
      outline: -webkit-focus-ring-color auto ${theme.border.size['1']};
    }

    &:disabled {
      cursor: not-allowed;
    }
  `

  const appearanceStyles: Record<ButtonUseKeys, RuleSet> = {
    'primary-blue': css`
      color: ${theme.colors.white};
      background: ${theme.colors.blue500};
      border-radius: ${theme.border.radius['12']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible,
        &:active {
          background: ${theme.colors.blue500};
        }

        &:disabled {
        }
      `
      }
    `,
    'primary-green': css`
      color: ${theme.colors.white};
      background: ${theme.colors.green500};
      border-radius: ${theme.border.radius['12']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible,
        &:active {
          background: ${theme.colors.green500};
        }

        &:disabled {
        }
      `
      }
    `,
    'primary-orange': css`
      color: ${theme.colors.white};
      background: ${theme.colors.orange500};
      border-radius: ${theme.border.radius['12']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:focus-visible,
        &:active {
          background: ${theme.colors.orange500};
        }

        &:disabled {
        }
      `
      }
    `,
    secondary: css`
      background: ${theme.colors.white};
      color: ${theme.colors.black500};
      border: ${theme.border.size['1']} solid ${theme.colors.gray500};
      border-radius: ${theme.border.radius['12']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:active {
          color: ${theme.colors.black500};
          border-color: ${theme.colors.gray500};
          background: ${theme.colors.white};
        }

        &:focus-visible {
          border-color: ${theme.colors.gray500};
        }

        &:disabled {
        }
      `
      }
    `,
    'secondary-danger': css`
      background: ${theme.colors.white};
      color: ${theme.colors.red500};
      border: ${theme.border.size['1']} solid ${theme.colors.gray500};
      border-radius: ${theme.border.radius['12']};

      ${
        !$overrideDisabled &&
        css`
        &:hover,
        &:active {
          color: ${theme.colors.red500};
          border-color: ${theme.colors.gray500};
          background: ${theme.colors.white};
        }

        &:focus-visible {
          border-color: ${theme.colors.gray500};
        }

        &:disabled {
        }
      `
      }
    `,
    unset: css``,
  }

  const isSecondary = $use === 'secondary' || $use === 'secondary-danger'
  const borderSize = isSecondary ? theme.border.size['1'] : theme.border.size['0']
  const sizeStyles: Record<ButtonSizeKeys, RuleSet> = {
    large: css`
      font-size: 1.6rem;
      line-height: 2.4rem;
      padding: calc(${theme.space['16']} - ${borderSize})
        calc(${$onlyBox ? theme.space['16'] : theme.space['24']} - ${borderSize});
    `,
    medium: css`
      font-size: 1.4rem;
      line-height: 2.4rem;
      padding: calc(${theme.space['12']} - ${borderSize})
        calc(${$onlyBox ? theme.space['12'] : theme.space['24']} - ${borderSize});
    `,
    small: css`
      font-size: 1.2rem;
      line-height: 2rem;
      padding: calc(${theme.space['10']} - ${borderSize})
        calc(${$onlyBox ? theme.space['10'] : theme.space['16']} - ${borderSize});
    `,
    unset: css``,
  }

  const loadingStyles = $overrideDisabled
    ? css`
        & > *:not([data-button-loader]) {
          visibility: hidden;
        }
      `
    : ''

  return css`
    ${baseStyles}
    ${appearanceStyles[$use]}
    ${sizeStyles[$size]}
    ${loadingStyles}
  `
})

export const ChildrenWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.space['4']};
  `,
)
